import { useMemo } from 'react';
import dayjs from 'dayjs';
import { FeatureTrialDateRange } from '@frontend/api-platform-location-feature';
import { useCallIntelTrialStats } from '@frontend/call-intel-core';

type UseCallIntelExpiredTrialStatsParams = {
  locationIds: string[];
  trialDateRangeMap: Record<string, FeatureTrialDateRange>;
};

export const useCallIntelExpiredTrialStats = ({
  locationIds,
  trialDateRangeMap,
}: UseCallIntelExpiredTrialStatsParams) => {
  const { minimumTrialStartDate, maximumTrialEndDate, trialDurationDays } = useMemo(() => {
    const startDates = locationIds
      .map((locationId) => trialDateRangeMap[locationId]?.startDate)
      .filter((date): date is string => !!date)
      .map((date) => new Date(date));

    const endDates = locationIds
      .map((locationId) => trialDateRangeMap[locationId]?.endDate)
      .filter((date): date is string => !!date)
      .map((date) => new Date(date));

    /**
     * Calculate the duration of each location's trial separately.
     * If the selected locations have different trial lengths, avoid showing
     * a single duration that may not apply to every location.
     */
    const trialDurations = locationIds
      .map((locationId) => {
        const { startDate, endDate } = trialDateRangeMap[locationId] ?? {};

        if (!startDate || !endDate) {
          return null;
        }

        return dayjs(endDate).diff(dayjs(startDate), 'day');
      })
      .filter((duration): duration is number => duration !== null);

    const uniqueTrialDurations = [...new Set(trialDurations)];

    return {
      minimumTrialStartDate:
        startDates.length > 0 ? new Date(Math.min(...startDates.map((date) => date.getTime()))) : null,

      maximumTrialEndDate: endDates.length > 0 ? new Date(Math.max(...endDates.map((date) => date.getTime()))) : null,
      // Only show a duration when it applies to every selected expired location.
      trialDurationDays:
        trialDurations.length === locationIds.length && uniqueTrialDurations.length === 1
          ? uniqueTrialDurations[0]
          : null,
    };
  }, [locationIds, trialDateRangeMap]);

  const { unhappyCalls, revenueAtRisk, schedulingRate, isLoading } = useCallIntelTrialStats({
    locationIds,
    minimumTrialStartDate,
    maximumTrialEndDate,
  });

  const hasNoTrialValue = !isLoading && unhappyCalls === 0 && revenueAtRisk === 0 && schedulingRate === 0;

  return {
    unhappyCalls,
    revenueAtRisk,
    schedulingRate,
    hasNoTrialValue,
    trialDurationDays,
    isLoading,
  };
};
